import axios from 'axios';
import { createKeybindingsHandler } from 'tinykeys';
import { nextEventLoop } from 'helpers/timing';
import debounce from 'lodash.debounce';
import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static values = {
    src: String,
    addUrl: String,
    managePhrasesUrl: {
      type: String,
      default: '/quick-phrases',
    },
    addPhraseUrl: String,
    loading: {
      type: String,
      default: 'lazy',
    },
  };

  static targets = [
    'tabShortCut',
    'errorTemplate',
    'addSuccessIndicator',
    'addQuickPhraseButton',
    'addFormErrors',
    'phraseAbbreviation',
    'searchBar',
    'newQuickPhrase',
    'phraseText',
    'loading',
    'phraseInput',
    'quickPhraseModal',
    'searchResults',
    'addQuickPhraseForm',
    'closePaletteMessage',
    'closeNewPhraseForm',
  ];

  controller = new AbortController();

  initialize() {
    // remove all actives first
    document.querySelectorAll('.quick-phrase-items.active').forEach((item) => {
      item.classList.remove('active');
    });

    this.handler = createKeybindingsHandler({
      Tab: (event) => {
        if (this.quickPhraseModalTarget.classList.contains('d-block')) {
          this.addPhrase();
          if (document.activeElement.dataset.quickPhrasesTarget === 'phraseText') {
            this.phraseAbbreviationTarget.focus();
            event.preventDefault();
            event.stopPropagation();
          }
        }
      },
      '$mod+Shift+p': async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const el = document.activeElement;
        // We allow for Input, Textarea, trix-editor and now CKEditor 5
        if (
          ['TRIX-EDITOR', 'TEXTAREA', 'INPUT'].includes(el.nodeName) ||
          el.isContentEditable ||
          el.closest('[contenteditable="true"]')
        ) {
          document
            .querySelectorAll('textarea, input, trix-editor, [contenteditable="true"]')
            .forEach((n) => n.removeAttribute('data-lastactive'));

          const editable = el.closest('[contenteditable="true"]') || el;
          editable.setAttribute('data-lastactive', 'true');
          if ('selectionStart' in editable) {
            editable.setAttribute('data-insertpoint', editable.selectionStart);
          }
          this.quickPhraseModalTarget.classList.toggle('d-block');
          await this.makeRequest();

          // focus on the input
          this.phraseInputTarget.focus();
          this.phraseInputTarget.select();
        }
      },
      '$mod+m': async (event) => {
        if (this.quickPhraseModalTarget.classList.contains('d-block')) {
          event.preventDefault();
          Turbo.visit(this.managePhrasesUrlValue);
        }
      },
      Escape: () => {
        if (!this.addQuickPhraseFormTarget.classList.contains('d-none')) {
          this.addQuickPhraseFormTarget.classList.add('d-none');
          this.newQuickPhraseTarget.classList.add('d-none');
          this.searchResultsTarget.classList.remove('d-none');
          this.searchBarTarget.classList.remove('d-none');
          this.clearAddPhraseForm();

          // focus on input
          this.phraseInputTarget.focus();
          this.phraseInputTarget.select();
        } else {
          this.quickPhraseModalTarget.classList.remove('d-block');
          this.clearSearchPhraseForm();
          this.refocusLastActiveElementOnClose();
        }

        this.tabShortCutTarget.classList.remove('d-none');
        this.closePaletteMessageTarget.classList.remove('d-none');
        this.closeNewPhraseFormTarget.classList.add('d-none');
      },
      '$mod+Enter': (event) => {
        if (this.quickPhraseModalTarget.classList.contains('d-block')) {
          this.tabShortCutTarget.classList.remove('d-none');
          this.closePaletteMessageTarget.classList.remove('d-none');
          this.closeNewPhraseFormTarget.classList.add('d-none');
          this.createQuickPhrase(event);
        }
      },
      Enter: (event) => {
        if (this.quickPhraseModalTarget.classList.contains('d-block')) {
          event.preventDefault();
          this.selectActivePhrase();
        }
      },
      arrowUp: (event) => {
        if (this.quickPhraseModalTarget.classList.contains('d-block')) {
          event.preventDefault();
          this.moveChoiceUp();
        }
      },
      arrowDown: (event) => {
        if (this.quickPhraseModalTarget.classList.contains('d-block')) {
          event.preventDefault();
          this.moveChoiceDown();
        }
      },
    });

    window.addEventListener('keydown', this.handler);

    this.intersectionObserver = new IntersectionObserver(this.intersect.bind(this));

    // if (this.loadingValue === 'eager') this.makeRequest();
    if (this.loadingValue === 'lazy') this.intersectionObserver.observe(this.element);
  }

  disconnect() {
    document.querySelectorAll('.quick-phrase-items.active').forEach((item) => {
      item.classList.remove('active');
    });

    window.removeEventListener('keydown', this.handler);
  }

  selectActivePhrase() {
    this.activeQuickPhrase.click();
  }

  clearSearchPhraseForm() {
    this.phraseInputTarget.value = '';
    this.addSuccessIndicatorTarget.classList.add('d-none');
    // eslint-disable-next-line prefer-destructuring
    this.srcValue = this.srcValue.split('?')[0];
  }

  clearAddPhraseForm() {
    this.phraseAbbreviationTarget.value = '';
    this.phraseTextTarget.value = '';
  }

  moveChoiceDown() {
    const currentItemIdx = Number(this.activeQuickPhrase.getAttribute('data-quick-phrases-idx-param'));
    const totalItems = Number(this.activeQuickPhrase.getAttribute('data-quick-phrases-items-param'));
    let nextItemIdx = currentItemIdx + 1;

    if (nextItemIdx > totalItems - 1) {
      nextItemIdx = 0;
    }

    document.querySelectorAll('.quick-phrase-items').forEach((item) => {
      item.classList.remove('active');
    });

    document.querySelector(`[data-quick-phrases-idx-param="${nextItemIdx}"]`).classList.add('active');
  }

  moveChoiceUp() {
    const currentItemIdx = Number(this.activeQuickPhrase.getAttribute('data-quick-phrases-idx-param'));
    const totalItems = Number(this.activeQuickPhrase.getAttribute('data-quick-phrases-items-param'));
    let nextItemIdx = currentItemIdx - 1;

    if (nextItemIdx < 0) {
      nextItemIdx = totalItems - 1;
    }

    document.querySelectorAll('.quick-phrase-items').forEach((item) => {
      item.classList.remove('active');
    });

    document.querySelector(`[data-quick-phrases-idx-param="${nextItemIdx}"]`).classList.add('active');
  }

  toggleModal(e) {
    e.preventDefault();
    e.stopPropagation();

    // don't just toggle modal, revert to start
    if (!this.addQuickPhraseFormTarget.classList.contains('d-none')) {
      this.addQuickPhraseFormTarget.classList.add('d-none');
      this.newQuickPhraseTarget.classList.add('d-none');
      this.searchResultsTarget.classList.remove('d-none');
      this.searchBarTarget.classList.remove('d-none');
      this.tabShortCutTarget.classList.remove('d-none');
      this.closePaletteMessageTarget.classList.remove('d-none');
      this.closeNewPhraseFormTarget.classList.add('d-none');
      this.clearAddPhraseForm();
      this.clearSearchPhraseForm();
    }

    this.quickPhraseModalTarget.classList.toggle('d-block');
    this.refocusLastActiveElementOnClose();
  }

  refocusLastActiveElementOnClose() {
    if (!this.quickPhraseModalTarget.classList.contains('d-block')) {
      const field = this.quickPhraseInputAreas;
      if (!field) return;
      // For CKEditor or contenteditable fields
      if (field.isContentEditable) {
        field.focus();
        return;
      }
      // For normal inputs/textarea
      const afterInsertionPoint = Number(field.dataset.insertpoint || 0);
      field.focus();
      if (typeof field.select === 'function') field.select();
      if (typeof field.setSelectionRange === 'function') {
        field.setSelectionRange(afterInsertionPoint, afterInsertionPoint);
      }
    }
  }

  srcValueChanged(newValue, oldValue) {
    // make request only when the oldValue was present and it changed
    if (oldValue && newValue) {
      debounce(() => {
        this.makeRequest();
      }, 250)();
    }
  }

  async choose(event) {
    event.preventDefault();
    event.stopPropagation();
    const phrase = await event.params.phrase;
    this.addPhraseToInput(` ${phrase} `);
    this.clearSearchPhraseForm();
  }

  get activeQuickPhrase() {
    return document.querySelectorAll('.quick-phrase-items.active')[0];
  }

  addPhraseToInput(phrase) {
    const field = this.quickPhraseInputAreas;
    if (!field) return;
    // Trix
    if (field.editor) {
      field.editor.insertString(this.smartInsertForTrix(phrase));
      field.focus();
    }
    // Normal textareas/inputs
    else if ('value' in field) {
      this.smartPhraseInsertTextFields(phrase);
    }
    // CKEditor
    else if (typeof field === 'object' && field.ui && field.model && typeof field.getData === 'function') {
      this.smartInsertForCKEditor(field, phrase);
    }
    // make the first item active
    document.querySelectorAll('.quick-phrase-items').forEach((item) => item.classList.remove('active'));
    document.querySelector('[data-quick-phrases-idx-param="0"]').classList.add('active');
    // close modal
    this.quickPhraseModalTarget.classList.toggle('d-block');
  }

  smartInsertForTrix(phrase) {
    const plaintText = this.quickPhraseInputAreas.editor.getDocument().toString();
    const beforePhrase = plaintText.slice(0, this.quickPhraseInputAreas.editor.getSelectedRange()[0]);
    const afterPhrase = plaintText.slice(this.quickPhraseInputAreas.editor.getSelectedRange()[1]);

    let newPhrase = beforePhrase.match(/(\s{1,}$)/g) ? `${phrase.trimStart()}` : phrase;
    newPhrase = afterPhrase.match(/(^\s{1,})/g) ? `${newPhrase.trimEnd()}` : newPhrase;

    let stringToInsert = `${
      beforePhrase.length === 0 ? this.capitalizeFirstLetter(newPhrase) : `${this.downcaseFirstLetter(newPhrase)}`
    }`;

    // capitalize if adding after a sentence ends
    stringToInsert =
      ['.', '?', '!'].includes(
        beforePhrase.trim().substring(beforePhrase.trim().length - 1, beforePhrase.trim().length)
      ) || beforePhrase.match(/(\n{1,}$)/g)
        ? ` ${this.capitalizeFirstLetter(stringToInsert.trim())}`
        : stringToInsert;

    // downcase if adding in a continuing sentence
    stringToInsert =
      !['.', '?', '!'].includes(
        beforePhrase.trim().substring(beforePhrase.trim().length - 1, beforePhrase.trim().length)
      ) && !beforePhrase.match(/(\n{1,}$)/g)
        ? ` ${this.downcaseFirstLetter(stringToInsert.trim())}`
        : stringToInsert;

    stringToInsert = beforePhrase.match(/(\s{1,}$)/g) ? `${stringToInsert.trimStart()}` : stringToInsert;

    // add space if there is text after inserted string
    stringToInsert = `${afterPhrase.length === 0 ? stringToInsert : `${stringToInsert.trimEnd()}`}`;

    if (plaintText.trim().length === 0) {
      stringToInsert = this.capitalizeFirstLetter(stringToInsert.trim());
    }

    return stringToInsert;
  }

  smartInsertForCKEditor(field, phrase) {
    const editor = window.lastActiveCKEditor;
    if (editor && typeof editor === 'object' && editor.ui && editor.model && typeof editor.getData === 'function') {
      editor.editing.view.focus();
      editor.model.change((writer) => {
        const insertable = writer.createText(phrase);
        editor.model.insertContent(insertable);
      });
    }
  }

  smartPhraseInsertTextFields(phrase) {
    let afterInsertionPoint = Number(this.quickPhraseInputAreas.dataset.insertpoint) + phrase.trim().length;

    const beforePhrase = this.quickPhraseInputAreas.value.slice(0, this.quickPhraseInputAreas.dataset.insertpoint);

    const afterPhrase = this.quickPhraseInputAreas.value.slice(this.quickPhraseInputAreas.dataset.insertpoint);

    let newPhrase = beforePhrase.match(/(\s{1,}$)/g) ? `${phrase.trimStart()}` : phrase;
    newPhrase = afterPhrase.match(/(^\s{1,})/g) ? `${newPhrase.trimEnd()}` : newPhrase;

    let stringToInsert = `${
      beforePhrase.length === 0 ? this.capitalizeFirstLetter(newPhrase) : `${this.downcaseFirstLetter(newPhrase)}`
    }`;

    // capitalize if adding after a sentence ends
    stringToInsert =
      ['.', '?', '!'].includes(
        beforePhrase.trim().substring(beforePhrase.trim().length - 1, beforePhrase.trim().length)
      ) || beforePhrase.match(/(\n{1,}$)/g)
        ? ` ${this.capitalizeFirstLetter(stringToInsert.trim())}`
        : stringToInsert;

    // downcase if adding in a continuing sentence
    stringToInsert =
      !['.', '?', '!'].includes(
        beforePhrase.trim().substring(beforePhrase.trim().length - 1, beforePhrase.trim().length)
      ) && !beforePhrase.match(/(\n{1,}$)/g)
        ? ` ${this.downcaseFirstLetter(stringToInsert.trim())}`
        : stringToInsert;

    stringToInsert = beforePhrase.match(/(\s{1,}$)/g) ? `${stringToInsert.trimStart()}` : stringToInsert;

    afterInsertionPoint += stringToInsert.length - stringToInsert.trim().length;

    // add space if there is text after inserted string
    stringToInsert = `${afterPhrase.length === 0 ? stringToInsert : `${stringToInsert}`}`;

    if (this.quickPhraseInputAreas.value.trim().length === 0) {
      stringToInsert = this.capitalizeFirstLetter(stringToInsert.trim());
    }

    this.quickPhraseInputAreas.value = beforePhrase + stringToInsert + afterPhrase;

    this.quickPhraseInputAreas.focus();
    this.quickPhraseInputAreas.setSelectionRange(afterInsertionPoint, afterInsertionPoint);
  }

  capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  downcaseFirstLetter(string) {
    return string.charAt(0).toLowerCase() + string.slice(1);
  }

  get quickPhraseInputAreas() {
    // First try the element with data-lastactive
    const el = document.querySelector('[data-lastactive="true"]');
    if (el) return el;
    // If no DOM element found, fall back to the last active CKEditor 5 instance
    if (window.lastActiveCKEditor) return window.lastActiveCKEditor;
    return null;
  }

  toggleSearchAndAdd() {
    this.searchResultsTarget.classList.toggle('d-none');
    this.addQuickPhraseFormTarget.classList.toggle('d-none');

    if (!this.searchResultsTarget.classList.contains('d-none')) {
      this.phraseInputTarget.focus();
      this.phraseInputTarget.select();
    } else {
      this.searchBarTarget.classList.add('d-none');
      this.newQuickPhraseTarget.classList.remove('d-none');
    }

    if (!this.addQuickPhraseFormTarget.classList.contains('d-none')) {
      this.phraseAbbreviationTarget.focus();
      this.phraseAbbreviationTarget.select();
    } else {
      this.searchBarTarget.classList.remove('d-none');
      this.addFormErrorsTarget.classList.add('d-none');
      this.newQuickPhraseTarget.classList.add('d-none');
    }
  }

  async addPhrase() {
    this.tabShortCutTarget.classList.add('d-none');
    this.closePaletteMessageTarget.classList.add('d-none');
    this.closeNewPhraseFormTarget.classList.remove('d-none');
    this.addSuccessIndicatorTarget.classList.add('d-none');
    this.searchResultsTarget.classList.add('d-none');
    this.searchBarTarget.classList.add('d-none');
    this.addQuickPhraseFormTarget.classList.remove('d-none');
    this.newQuickPhraseTarget.classList.remove('d-none');
    this.phraseTextTarget.value = this.phraseInputTarget.value;
  }

  async createQuickPhrase(event) {
    this.addQuickPhraseButtonTarget.classList.add('disabled');
    event.preventDefault();
    event.stopPropagation();

    await axios
      .post('/quick-phrases.json', {
        quick_phrase: {
          abbreviation: this.phraseAbbreviationTarget.value,
          text: this.phraseTextTarget.value,
        },
      })
      .then((response) => {
        this.quickPhraseCreateSuccess(response);
        this.clearAddPhraseForm();
      })
      .catch((error) => {
        this.quickPhraseCreateFailure(error);
      });
  }

  quickPhraseCreateSuccess(response) {
    this.phraseInputTarget.value = response.data.abbreviation;
    this.addSuccessIndicatorTarget.classList.remove('d-none');
    this.search(undefined, 'createQuickPhrase');
    this.addQuickPhraseButtonTarget.classList.remove('disabled');
    this.toggleSearchAndAdd();
  }

  quickPhraseCreateFailure(error) {
    this.addQuickPhraseButtonTarget.classList.remove('disabled');
    this.addFormErrorsTarget.textContent = error.response.data.join(', ');
    this.addFormErrorsTarget.classList.remove('d-none');
  }

  search(event, comingFrom = 'searchInput') {
    if (comingFrom !== 'createQuickPhrase') {
      this.addSuccessIndicatorTarget.classList.add('d-none');
    }

    const url = new URL(`${this.srcValue}`);
    const searchPs = url.searchParams;
    const query = event === undefined ? this.phraseInputTarget.value : event.target.value;

    searchPs.set('query', query);
    url.search = searchPs.toString();

    this.srcValue = url.toString();
  }

  async makeRequest() {
    this.loadingTarget.classList.remove('d-none');
    if (!this.hasSrcValue || !this.srcValue) return;

    this.controller?.abort();
    this.controller = new AbortController();

    this.clearObserver();
    try {
      const data = await makeNetworkRequest(this.element, this.srcValue, {
        headers: {
          accept: 'text/fragment+html',
          credentials: 'same-origin',
        },
        signal: this.controller.signal,
      });
      this.targetContainer.innerHTML = data;
      this.dispatch('success', { bubbles: true });
      this.controller = null;
    } catch (error) {
      if (!axios.isCancel(error)) {
        this.dispatch('error', { bubbles: true });
        this.controller = null;
      }
    }
    this.loadingTarget.classList.add('d-none');
  }

  // Private

  intersect(entries) {
    const lastEntry = entries.slice(-1)[0];
    if (lastEntry?.isIntersecting) {
      const { target } = lastEntry;

      this.clearObserver(target);
      this.makeRequest();
    }
  }

  clearObserver(target = this.element) {
    this.intersectionObserver?.unobserve(target);
  }

  get targetContainer() {
    return document.getElementById('quick-phrases-results-container');
  }
}

async function makeNetworkRequest(el, src, options) {
  // Mimic `<img>` event dispatch order ['loadstart', 'load/error', 'loadend']
  try {
    el.dispatchEvent(new CustomEvent('loadstart'));
    const { data } = await axios.get(src, options);
    el.dispatchEvent(new CustomEvent('load'));
    el.dispatchEvent(new CustomEvent('loadend'));
    await nextEventLoop();
    document.getElementById('quickPhrasesErrorTemplate').classList.add('d-none');
    return data;
  } catch (error) {
    // el.dispatchEvent(new CustomEvent('error'));
    if (error.message !== undefined) {
      document.getElementById('quickPhrasesErrorTemplate').classList.remove('d-none');
    }
    el.dispatchEvent(new CustomEvent('loadend'));
    await nextEventLoop();
    throw error;
  }
}
